/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Reorder } from 'framer-motion';
import { Edit2, Trash2, Check, X, Plus } from 'lucide-react';
import { serviceRequestService } from '../../services/serviceRequestService';
import toastService from '../../utils/toastService';
import {
  SERVICE_REQUEST_CHECKLIST_TITLE_MAX_LENGTH,
  SERVICE_REQUEST_CHECKLIST_ITEM_TITLE_MAX_LENGTH,
} from '../../constants/serviceRequests';
import ChecklistItemRow from './ChecklistItemRow';
import ChecklistDeleteModal from './ChecklistDeleteModal';
import ChecklistIconTooltip from './ChecklistIconTooltip';
import { apiErrorMessage, checklistPermissionsShape } from './checklistHelpers';

const orderKey = (items) => items.map((i) => i.uid).join(',');

/**
 * One checklist: editable title, progress, drag-reorderable items and an add-item
 * input. `onUpdate(updater)` replaces this checklist in the parent's state.
 */
const ChecklistCard = ({
  checklist,
  serviceRequestUid,
  permissions,
  onUpdate,
  onDelete,
  onReload,
  onChanged,
}) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [itemDraft, setItemDraft] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [itemDeleteTarget, setItemDeleteTarget] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);

  // The latest items (for the drag-end handler) and the order when a drag began,
  // so a drag that ends where it started doesn't call the API.
  const itemsRef = useRef(checklist.items);
  itemsRef.current = checklist.items;
  const orderAtDragStartRef = useRef(null);

  // The item input is disabled while saving, so focus is put back once it's
  // enabled again, letting the user type the next item straight away.
  const itemInputRef = useRef(null);
  const refocusItemInputRef = useRef(false);
  useEffect(() => {
    if (!addingItem && refocusItemInputRef.current) {
      refocusItemInputRef.current = false;
      itemInputRef.current?.focus();
    }
  }, [addingItem]);

  const items = checklist.items;
  const completedCount = items.filter((i) => i.isCompleted).length;
  const progress = items.length ? Math.round((completedCount / items.length) * 100) : 0;

  const setItems = (updater) =>
    onUpdate((c) => ({ ...c, items: typeof updater === 'function' ? updater(c.items) : updater }));

  const startEditTitle = () => {
    setTitleDraft(checklist.title);
    setEditingTitle(true);
  };

  const handleSaveTitle = async (e) => {
    e.preventDefault();
    const title = titleDraft.trim();
    if (!title) return;
    if (title === checklist.title) {
      setEditingTitle(false);
      return;
    }
    try {
      setSavingTitle(true);
      const { data } = await serviceRequestService.updateChecklist(serviceRequestUid, checklist.uid, title);
      onUpdate((c) => ({ ...c, ...data, items: c.items }));
      setEditingTitle(false);
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to rename checklist'));
    } finally {
      setSavingTitle(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    const title = itemDraft.trim();
    if (!title) return;
    refocusItemInputRef.current = true;
    try {
      setAddingItem(true);
      const { data } = await serviceRequestService.addChecklistItem(serviceRequestUid, checklist.uid, title);
      setItems((prev) => [...prev, data]);
      setItemDraft('');
      onChanged();
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to add item'));
    } finally {
      setAddingItem(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!itemDeleteTarget) return;
    try {
      setDeletingItem(true);
      await serviceRequestService.deleteChecklistItem(serviceRequestUid, checklist.uid, itemDeleteTarget.uid);
      setItems((prev) => prev.filter((i) => i.uid !== itemDeleteTarget.uid));
      setItemDeleteTarget(null);
      toastService.success('Checklist item deleted successfully');
      onChanged();
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to delete item'));
    } finally {
      setDeletingItem(false);
    }
  };

  const handleDragStart = () => {
    orderAtDragStartRef.current = orderKey(itemsRef.current);
  };

  const handleDragEnd = async () => {
    const current = itemsRef.current;
    if (orderKey(current) === orderAtDragStartRef.current) return;
    orderAtDragStartRef.current = null;
    try {
      const { data } = await serviceRequestService.reorderChecklistItems(
        serviceRequestUid, checklist.uid, current.map((i) => i.uid),
      );
      setItems(data);
    } catch (err) {
      // 409 means the list changed elsewhere; either way, resync with the server.
      toastService.error(apiErrorMessage(err, 'Failed to save item order'));
      onReload();
    }
  };

  const handleItemUpdated = (updated) =>
    setItems((prev) => prev.map((i) => (i.uid === updated.uid ? updated : i)));

  const handleCommentCountChange = (itemUid, delta) =>
    setItems((prev) => prev.map((i) => (
      i.uid === itemUid ? { ...i, commentCount: Math.max(0, (i.commentCount || 0) + delta) } : i
    )));

  return (
    <div className="rounded-xl border border-base-content/10 bg-base-200/20 p-3 sm:p-4">
      {/* Title + actions */}
      <div className="flex items-start gap-2 mb-2">
        {editingTitle ? (
          <form onSubmit={handleSaveTitle} className="flex items-center gap-1.5 flex-1 min-w-0">
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditingTitle(false);
              }}
              maxLength={SERVICE_REQUEST_CHECKLIST_TITLE_MAX_LENGTH}
              aria-label="Checklist title"
              autoFocus
              disabled={savingTitle}
              className="input input-sm input-bordered flex-1 min-w-0 rounded-lg text-sm font-bold focus:outline-none focus:border-primary"
            />
            <ChecklistIconTooltip tip="Save">
              <button type="submit" aria-label="Save" disabled={savingTitle || !titleDraft.trim()} className="btn btn-xs btn-primary btn-square rounded-lg">
                {savingTitle ? <span className="loading loading-spinner loading-xs"></span> : <Check size={12} />}
              </button>
            </ChecklistIconTooltip>
            <ChecklistIconTooltip tip="Cancel">
              <button type="button" aria-label="Cancel" onClick={() => setEditingTitle(false)} disabled={savingTitle} className="btn btn-xs btn-ghost btn-square rounded-lg">
                <X size={12} />
              </button>
            </ChecklistIconTooltip>
          </form>
        ) : (
          <>
            <h3 className="flex-1 min-w-0 text-sm font-black text-base-content break-words">{checklist.title}</h3>
            <span className="text-xs font-bold text-base-content/50 whitespace-nowrap mt-0.5">
              {completedCount}/{items.length}
            </span>
            {permissions.rename && (
              <ChecklistIconTooltip tip="Rename">
                <button
                  type="button"
                  onClick={startEditTitle}
                  aria-label="Rename checklist"
                  className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                >
                  <Edit2 size={13} />
                </button>
              </ChecklistIconTooltip>
            )}
            {permissions.delete && (
              <ChecklistIconTooltip tip="Delete checklist" variant="error" position="left">
                <button
                  type="button"
                  onClick={() => onDelete(checklist)}
                  aria-label="Delete checklist"
                  className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                >
                  <Trash2 size={13} />
                </button>
              </ChecklistIconTooltip>
            )}
          </>
        )}
      </div>

      {/* Progress */}
      <div className="h-1.5 rounded-full bg-base-content/10 overflow-hidden mb-3" aria-hidden="true">
        <div
          className={`h-full rounded-full transition-all ${progress === 100 ? 'bg-success' : 'bg-primary'}`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Items */}
      {items.length === 0 ? (
        <p className="text-xs text-base-content/40 py-2">No items yet.</p>
      ) : (
        <Reorder.Group as="div" axis="y" values={items} onReorder={setItems} className="space-y-1.5">
          {items.map((item, index) => (
            <ChecklistItemRow
              key={item.uid}
              item={item}
              number={index + 1}
              serviceRequestUid={serviceRequestUid}
              permissions={permissions}
              onUpdated={handleItemUpdated}
              onDelete={setItemDeleteTarget}
              onCommentCountChange={handleCommentCountChange}
              onChanged={onChanged}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />
          ))}
        </Reorder.Group>
      )}

      {/* Add item */}
      {permissions.itemAdd && (
        <form onSubmit={handleAddItem} className="flex items-center gap-2 mt-3">
          <input
            type="text"
            ref={itemInputRef}
            value={itemDraft}
            onChange={(e) => setItemDraft(e.target.value)}
            maxLength={SERVICE_REQUEST_CHECKLIST_ITEM_TITLE_MAX_LENGTH}
            placeholder="Add an item..."
            aria-label="New checklist item"
            disabled={addingItem}
            className="input input-sm input-bordered flex-1 min-w-0 rounded-lg text-sm bg-base-100 focus:outline-none focus:border-primary"
          />
          <button type="submit" disabled={addingItem || !itemDraft.trim()} className="btn btn-sm btn-primary rounded-lg gap-1 font-bold">
            {addingItem ? <span className="loading loading-spinner loading-xs"></span> : <Plus size={14} />}
            Add
          </button>
        </form>
      )}

      <ChecklistDeleteModal
        isOpen={!!itemDeleteTarget}
        title="Delete Checklist Item"
        message="Are you sure you want to delete this item?"
        preview={itemDeleteTarget?.title}
        note="Its comments will be deleted too."
        deleting={deletingItem}
        onCancel={() => setItemDeleteTarget(null)}
        onConfirm={handleDeleteItem}
      />
    </div>
  );
};

ChecklistCard.propTypes = {
  checklist: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
  }).isRequired,
  serviceRequestUid: PropTypes.string.isRequired,
  permissions: checklistPermissionsShape.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default ChecklistCard;
